const DB_NAME = 'smt-quick-expenses-v1';
let databasePromise;
const requestResult = (request) => new Promise((resolve, reject) => { request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error); });
const transactionDone = (transaction) => new Promise((resolve, reject) => { transaction.oncomplete = resolve; transaction.onerror = () => reject(transaction.error); transaction.onabort = () => reject(transaction.error || new Error('No se pudo guardar el borrador.')); });

const database = () => {
  if (!window.indexedDB || !window.crypto?.subtle) return Promise.reject(new Error('El almacenamiento seguro no está disponible en este dispositivo.'));
  if (!databasePromise) databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      const drafts = db.createObjectStore('drafts', { keyPath: ['userId', 'id'] });
      drafts.createIndex('userId', 'userId');
      db.createObjectStore('keys', { keyPath: 'userId' });
    };
    request.onsuccess = () => { request.result.onversionchange = () => { request.result.close(); databasePromise = null; }; resolve(request.result); };
    request.onerror = () => { databasePromise = null; reject(request.error); };
    request.onblocked = () => reject(new Error('Cierra otras ventanas de la app para habilitar los borradores.'));
  });
  return databasePromise;
};

const keyForUser = async (userId) => {
  const db = await database();
  let row = await requestResult(db.transaction('keys').objectStore('keys').get(userId));
  if (row) return row.key;
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  const tx = db.transaction('keys', 'readwrite'); const done = transactionDone(tx);
  const store = tx.objectStore('keys');
  row = await requestResult(store.get(userId));
  if (!row) store.put({ userId, key });
  await done;
  return row?.key || key;
};

const bytesToBase64 = (bytes) => {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 16384) binary += String.fromCharCode(...bytes.subarray(offset, offset + 16384));
  return btoa(binary);
};
const fileBytes = (file) => file.arrayBuffer ? file.arrayBuffer() : new Promise((resolve, reject) => {
  const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.onerror = reject; reader.readAsArrayBuffer(file);
});

export const newQuickExpenseId = () => {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16)); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const announce = () => window.dispatchEvent(new Event('quick-expenses:changed'));
export const listQuickExpenses = async (userId) => {
  const db = await database();
  const rows = await requestResult(db.transaction('drafts').objectStore('drafts').index('userId').getAll(String(userId)));
  return rows.sort((a, b) => b.updatedAt - a.updatedAt);
};

export const saveQuickExpense = async (userId, draft, { enqueue = false } = {}) => {
  userId = String(userId);
  const files = [];
  for (const file of draft.files || []) files.push({ name: file.name, type: file.type, lastModified: file.lastModified, data: bytesToBase64(new Uint8Array(await fileBytes(file))) });
  const key = await keyForUser(userId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(JSON.stringify({ fields: draft.fields, files })));
  const db = await database(); const tx = db.transaction('drafts', 'readwrite'); const done = transactionDone(tx);
  const store = tx.objectStore('drafts');
  const previous = await requestResult(store.get([userId, draft.id]));
  if (previous && previous.state !== 'draft') { await done; throw new Error('Este gasto ya está en la cola de envío y sus datos no pueden cambiar.'); }
  store.put({ userId, id: draft.id, iv, cipher, state: enqueue ? 'queued' : 'draft', updatedAt: Date.now(), leaseUntil: 0, attempts: 0, error: '' });
  await done; announce();
};

export const readQuickExpense = async (userId, id) => {
  const db = await database();
  const row = await requestResult(db.transaction('drafts').objectStore('drafts').get([String(userId), id]));
  if (!row) return null;
  const key = await keyForUser(String(userId));
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: row.iv }, key, row.cipher);
  const payload = JSON.parse(new TextDecoder().decode(plain));
  const files = payload.files.map((file) => new File([Uint8Array.from(atob(file.data), (char) => char.charCodeAt(0))], file.name, { type: file.type, lastModified: file.lastModified }));
  return { ...row, fields: payload.fields, files };
};

export const deleteQuickExpense = async (userId, id, { sent = false } = {}) => {
  const db = await database(); const tx = db.transaction('drafts', 'readwrite'); const done = transactionDone(tx); const store = tx.objectStore('drafts');
  const row = await requestResult(store.get([String(userId), id]));
  if (row && (sent || row.state === 'draft')) store.delete([String(userId), id]);
  await done; announce();
};

export const claimQuickExpense = async (userId) => {
  const db = await database(); const tx = db.transaction('drafts', 'readwrite'); const done = transactionDone(tx); const store = tx.objectStore('drafts');
  const rows = await requestResult(store.index('userId').getAll(String(userId)));
  const row = rows.find((item) => ['queued', 'sending'].includes(item.state) && item.leaseUntil < Date.now());
  if (row) store.put({ ...row, state: 'sending', leaseUntil: Date.now() + 120000, attempts: row.attempts + 1 });
  await done;
  if (row) announce();
  return row?.id || null;
};

export const finishQuickExpenseAttempt = async (userId, id, { error, retry = false } = {}) => {
  const db = await database(); const tx = db.transaction('drafts', 'readwrite'); const done = transactionDone(tx); const store = tx.objectStore('drafts');
  const row = await requestResult(store.get([String(userId), id]));
  if (row) store.put({ ...row, state: retry ? 'queued' : 'error', leaseUntil: 0, error: String(error || '').slice(0, 500) });
  await done; announce();
};
